import { FC, useState } from 'react'
import classNames from 'classnames'
import { CircleInfoIcon } from '@/atoms/Icons/CircleInfoIcon'
import { Popover } from '@/atoms/Popover'
import { ParagraphLG, ParagraphSM, Text, TitleLG } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { useIsDarkMode } from '@/contexts/ThemeContext'
import { ProjectFundingInfoModal } from '@/organisms/ProjectFundingGoalDisplay/ProjectFundingInfoModal'
import { useIncrementer } from '@/services/GoalService/hooks/useIncrementer'
import { TheatricalReleaseResult } from '@/services/Theatrical/queries'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'
import useTheatricalGoalService from '@/views/TicketCheckoutViews/hooks/useTheatricalGoalService'

interface TheatricalPifGoalLineProps {
  theatricalRelease: TheatricalReleaseResult
  fullWidth?: boolean
  className?: string
  shouldUseIncrementer?: boolean
  isPossumTrotExperiment?: boolean
  newPifLayout?: boolean
}

export const TheatricalPifGoalLine: FC<TheatricalPifGoalLineProps> = ({
  theatricalRelease,
  fullWidth = false,
  className,
  shouldUseIncrementer = false,
  isPossumTrotExperiment = false,
  newPifLayout = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const { t } = useTranslate('pif')
  const isDarkMode = useIsDarkMode()
  const { locale } = useLocale()
  const { name } = useProjectMeta()
  const theatricalSlug = theatricalRelease?.theatricalSlug
  const { goalLineInfo } = useTheatricalGoalService({
    theatricalSlug,
    releaseDate: theatricalRelease?.region?.releaseDate,
    isPossumTrotExperiment,
    usePifGoal: theatricalSlug === slugs.david,
  })
  const headerTextColor = isDarkMode ? 'white' : 'core-gray-950'
  const paragraphTextColor = isDarkMode ? 'core-gray-300' : 'core-gray-500'
  const fullWidthClass = !fullWidth ? '' : 'lg:flex-row lg:gap-14'
  const incrementerNumber = useIncrementer(goalLineInfo.ticketsSold, 0.75)
  const incrementerPercent = Math.floor((incrementerNumber / goalLineInfo.currentMilestone) * 100)
  const ticketsSold = shouldUseIncrementer ? Math.ceil(incrementerNumber) : goalLineInfo?.ticketsSold
  const ticketPercent = incrementerPercent

  return (
    <div
      className={classNames(
        'bg-white px-4 md:px-8 border border-core-gray-200 lg:mb-10 xl:mb-16',
        className,
        newPifLayout ? 'md:rounded-3xl' : 'md:rounded-xl',
      )}
    >
      <div className="flex flex-col lg:gap-6">
        <div className="flex w-full flex-row justify-between">
          <div
            className={classNames(
              'mg:mb-0 mb-3 flex w-full items-center justify-between gap-3 md:flex-row md:gap-0 flex-row',
            )}
          >
            {newPifLayout ? (
              <Text
                color={isDarkMode ? 'white' : 'core-gray-950'}
                weight="semibold"
                className="photon-title-sm md:photon-heading-xs"
              >
                {goalLineInfo?.goalLineTitle}
              </Text>
            ) : (
              <TitleLG color={isDarkMode ? 'white' : 'core-gray-950'} weight="bold" className="flex justify-between">
                {goalLineInfo?.goalLineTitle}
              </TitleLG>
            )}
            <Popover
              title={t('info', 'Info')}
              description={t(
                'totalTicketsPIFDisclaimer',
                'Total tickets are an estimate based on current box office sales. Pay it Forward is only included in the total when a free ticket is redeemed to watch in theaters.',
              )}
              icon={<CircleInfoIcon size={18} color="core-gray-400" />}
              triggerComponent={<CircleInfoIcon size={18} color="core-gray-500" />}
            />
          </div>
        </div>
        <div className={classNames('flex flex-col gap-1', fullWidthClass)}>
          <div className="flex flex-row justify-between lg:hidden">
            <div className="flex flex-col gap-1 lg:gap-2">
              <ParagraphSM color={headerTextColor} className="block lg:hidden" weight="semibold">
                {t('numberOfTickets', '{{numTickets}} Tickets', {
                  numTickets: new Intl.NumberFormat(locale).format(ticketsSold),
                })}
              </ParagraphSM>
            </div>
            <div className="flex flex-col gap-1 lg:gap-2">
              <ParagraphSM color={paragraphTextColor} className="block lg:hidden">
                {new Intl.NumberFormat(locale).format(goalLineInfo.currentMilestone)}{' '}
                {goalLineInfo?.isLastMilestone
                  ? t('totalTicketsGoalV6', 'Goal')
                  : t('goalLineMilestoneText', 'Milestone')}
              </ParagraphSM>
            </div>
          </div>
          <div className="flex basis-1/2 flex-col gap-2 lg:gap-6">
            <div className={classNames('w-full rounded-full', isDarkMode ? 'bg-core-gray-800' : 'bg-core-gray-200')}>
              <div
                className="h-2.5 rounded-full bg-oxide-700"
                style={{
                  width: `${ticketPercent > 100 ? '100%' : `${ticketPercent}%`}`,
                }}
              />
            </div>
            <div className="flex flex-row justify-between">
              <div className="hidden flex-col gap-1 lg:flex lg:gap-2">
                <TitleLG color={headerTextColor} className="hidden text-xl lg:block xl:text-2xl" weight="semibold">
                  {t('numberOfTickets', '{{numTickets}} Tickets', {
                    numTickets: new Intl.NumberFormat(locale).format(ticketsSold),
                  })}
                </TitleLG>
                <div className="flex flex-row items-center">
                  <ParagraphLG color={paragraphTextColor} className="hidden text-base lg:block xl:text-lg">
                    {t('ofTicketsGoalV7', 'of {{numTickets}} {{goalText}}', {
                      numTickets: new Intl.NumberFormat(locale).format(goalLineInfo.currentMilestone),
                      goalText: goalLineInfo?.isLastMilestone
                        ? t('totalTicketsGoalV6', 'Goal')
                        : t('goalLineMilestoneText', 'Milestone'),
                    })}
                  </ParagraphLG>
                </div>
              </div>
              <div className="flex flex-col justify-between gap-1 lg:ml-auto lg:gap-2">
                <ParagraphSM color={headerTextColor} className={`flex justify-start lg:hidden iPadPro105:hidden`}>
                  {ticketPercent}%{' '}
                  {goalLineInfo?.isLastMilestone ? t('ofGoal', 'of Goal') : t('ofGoalLineMilestone', 'of Milestone')}
                </ParagraphSM>
                <TitleLG
                  color={headerTextColor}
                  className="hidden justify-start text-xl !font-semibold xl:text-2xl iPadPro105:flex"
                >
                  {ticketPercent}%{' '}
                  {goalLineInfo?.isLastMilestone ? t('ofGoal', 'of Goal') : t('ofGoalLineMilestone', 'of Milestone')}
                </TitleLG>
              </div>

              <div className="lg:display min-[1042px]:hidden ml-auto flex flex-col gap-1 lg:gap-2 iPadPro105:hidden">
                <TitleLG
                  color={headerTextColor}
                  className="hidden justify-start text-xl !font-semibold lg:flex xl:text-2xl"
                >
                  {ticketPercent}%{' '}
                  {goalLineInfo?.isLastMilestone ? t('ofGoal', 'of Goal') : t('ofGoalLineMilestone', 'of Milestone')}
                </TitleLG>
              </div>
            </div>
          </div>
          <ProjectFundingInfoModal
            onClose={() => setIsModalOpen(false)}
            show={isModalOpen}
            goalName={name}
            isTheatrical={true}
          />
        </div>
      </div>
    </div>
  )
}
